<template>
    <div class="relative min-h-screen antialiased bg-white font-sans">
        <!-- Top right actions -->
        <div class="absolute right-5 md:right-20 top-4 flex gap-4">
            <button
                v-if="!isRetailer"
                v-print="printObj"
                title="Print"
                class="p-2 rounded-full hover:bg-gray-200"
            >
                <PrintIcon class="w-5" />
            </button>

            <button
                title="Reload"
                class="p-2 rounded-full hover:bg-gray-200"
                @click="reload"
            >
                <ReplaceIcon />
            </button>
        </div>

        <!-- Printable Body -->
        <div
            id="print-invoice"
            class="px-8 py-12 md:px-16 print:p-8"
        >
            <!-- ===== Header Band ===== -->
            <div class="relative">
                <!-- Top slanted band imitation -->
                <div class="bg-gray-100 border-t-4 border-gray-300 rounded-t-md">
                    <div class="px-5 py-4 flex items-start justify-between">
                        <div>
                            <h1 class="text-2xl font-bold leading-6">{{ bill?.wholesaler?.name }}</h1>
                            <p class="uppercase tracking-wide text-sm text-gray-700 mt-3">Wholesale Distributors</p>
                            <!-- Logo if present -->
                            <img
                                v-if="bill?.wholesaler?.profile_photo_url"
                                :src="bill.wholesaler.profile_photo_url"
                                class="max-h-full max-w-full"
                                alt="Logo"
                            />
                        </div>

                        <div class="text-right">
                            <div class="inline-block bg-white border border-gray-300 rounded px-3 py-2">
                                <div class="text-sm font-semibold">
                                    VAT REG No. -
                                    <span class="align-middle">{{
                                        bill?.wholesaler?.kyc.company_registration_number
                                    }}</span>
                                </div>
                            </div>

                            <div class="mt-3 flex items-center justify-center">
                                <div class="mt-2 text-xs leading-5 text-gray-700">
                                    <p>{{ bill?.wholesaler?.kyc.business_address }}</p>
                                    <p>Trinidad, Trinidad and Tobago</p>
                                    <p>Tel: {{ bill?.wholesaler?.kyc.phone }}</p>
                                    <p>Email: {{ bill?.wholesaler?.email }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- ===== Dual Shipping Address Blocks ===== -->
            <div class="grid grid-cols-2 gap-6 mt-4">
                <div>
                    <h3 class="uppercase text-sm font-bold text-gray-700">Billing Address</h3>
                    <div class="mt-1 p-3 border border-gray-300 rounded-md text-sm leading-5">
                        <p class="font-semibold">{{ bill?.shipping?.to_name || bill?.user?.name }}</p>
                        <p>
                            {{ bill?.shipping?.line1 || bill?.user?.kyc?.business_address }}
                        </p>
                        <p>
                            {{ bill?.shipping?.line2 || bill?.user?.kyc?.business_references_address }}
                        </p>
                        <p v-if="bill?.shipping?.city">{{ bill.shipping.city }}</p>
                        <p v-if="bill?.shipping?.country">{{ bill.shipping.country }}</p>
                    </div>
                </div>
                <div>
                    <h3 class="uppercase text-sm font-bold text-gray-700">Shipping Address</h3>
                    <div class="mt-1 p-3 border border-gray-300 rounded-md text-sm leading-5">
                        <p class="font-semibold">{{ bill?.shipping_alt?.to_name || bill?.user?.name }}</p>
                        <p>
                            {{ bill?.shipping?.line1 || bill?.user?.kyc?.business_address }}
                        </p>
                        <p>
                            {{ bill?.shipping?.line2 || bill?.user?.kyc?.business_references_address }}
                        </p>
                        <p v-if="bill?.shipping_alt?.city">{{ bill.shipping_alt.city }}</p>
                        <p v-if="bill?.shipping_alt?.country">{{ bill.shipping_alt.country }}</p>
                    </div>
                </div>
            </div>

            <!-- ===== Centered Invoice Title ===== -->
            <div class="text-right mt-6">
                <h2 class="text-2xl font-bold">
                    Invoice <span class="font-normal">INV/{{ bill?.bill_number }}</span>
                </h2>
                <p class="text-sm text-gray-700">Customer Copy</p>
            </div>

            <!-- ===== Meta Grid ===== -->
            <div class="grid grid-cols-5 gap-3 mt-4 text-sm">
                <div class="border border-gray-300 rounded p-2">
                    <p class="text-gray-600">Invoice Date</p>
                    <p class="font-semibold">{{ bill?.created_at | readableDate }}</p>
                </div>
                <div class="border border-gray-300 rounded p-2">
                    <p class="text-gray-600">Due Date</p>
                    <p class="font-semibold">{{ bill?.due_date | readableDate }}</p>
                </div>
                <div class="border border-gray-300 rounded p-2">
                    <p class="text-gray-600">Delivery Date</p>
                    <p class="font-semibold">{{ bill?.delivery_date | readableDate }}</p>
                </div>
                <div class="border border-gray-300 rounded p-2">
                    <p class="text-gray-600">Source</p>
                    <p class="font-semibold">{{ bill?.source || '-' }}</p>
                </div>
                <div class="border border-gray-300 rounded p-2">
                    <p class="text-gray-600">Method #</p>
                    <p class="font-semibold">
                        {{ bill?.payment_method }}
                    </p>
                </div>
            </div>

            <!-- ===== Description Table ===== -->
            <div class="mt-6">
                <table class="w-full border border-gray-300 text-sm text-left">
                    <thead class="bg-gray-100">
                        <tr class="text-gray-800">
                            <th class="p-2 border w-[8%] text-center">#</th>
                            <th class="p-2 border">Description</th>
                            <th class="p-2 border w-[12%] text-center">Quantity</th>
                            <th class="p-2 border w-[14%] text-right">Unit Price</th>
                            <th class="p-2 border w-[12%] text-right">Taxes</th>
                            <th class="p-2 border w-[14%] text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(t, i) in bill.transactions"
                            :key="t.id || i"
                            class="odd:bg-white even:bg-gray-50"
                        >
                            <td class="p-2 border text-center">{{ i + 1 }}</td>
                            <td class="p-2 border">
                                <!-- <span
                                    v-if="t.product?.code"
                                    class="text-gray-600"
                                    >[{{ t.product.code }}]
                                </span> -->
                                <span class="font-medium">{{ t.product?.name }}</span>
                            </td>
                            <td class="p-2 border text-center">{{ t.qty }}</td>
                            <td class="p-2 border text-right">
                                {{ config(common.currency) }} {{ num(t.product?.price) }}
                            </td>
                            <td class="p-2 border text-right">{{ config(common.currency) }} {{ num(t.vat) }}</td>
                            <td class="p-2 border text-right">
                                {{ config(common.currency) }} {{ num(t.grand_total) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- ===== Totals Box ===== -->
            <div class="mt-4 flex justify-between">
                <!-- ===== Prepared / Salesperson / Terms ===== -->
                <div class="grid grid-cols-1 gap-3 mt-3 text-sm w-full md:w-1/3">
                    <div class="flex">
                        <p class="text-gray-600">Prepared By:</p>
                        <p class="font-semibold px-2">{{ bill?.prepared_by || '' }}</p>
                    </div>
                    <div class="flex">
                        <p class="text-gray-600">Salesperson:</p>
                        <p class="font-semibold px-2">{{ bill?.salesperson || '' }}</p>
                    </div>
                    <div class="flex">
                        <p class="text-gray-600">Payment Terms:</p>
                        <p class="font-semibold px-2">{{ bill?.payment_terms || '30 Days' }}</p>
                    </div>
                </div>
                <div class="w-full md:w-1/3">
                    <table class="w-full text-sm">
                        <tbody>
                            <tr>
                                <td class="p-2 text-right text-gray-700 font-bold">Untaxed Amount</td>
                                <td class="p-2 text-right font-semibold border-b border-gray-300">
                                    {{ config(common.currency) }} {{ num(bill?.subtotal) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="p-2 text-right text-gray-700 font-bold">
                                    Tax {{ bill?.tax_rate || '12.5' }}%
                                </td>
                                <td class="p-2 text-right font-semibold border-b border-gray-300">
                                    {{ config(common.currency) }} {{ num(bill?.tax) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="p-2 text-right text-gray-900 font-bold">Total</td>
                                <td class="p-2 text-right font-bold bg-gray-800 text-white rounded">
                                    {{ config(common.currency) }} {{ num(bill?.total_amount) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Signatures -->
            <div class="mt-8 text-sm">
                <table class="w-1/2 md:w-2/5 border border-gray-300 text-sm">
                    <thead class="bg-gray-100 text-left">
                        <tr>
                            <th class="p-2 border w-64 text-left">Driver’s Name</th>
                            <th class="p-2 border w-40 text-left">Signature</th>
                            <th class="p-2 border w-40 text-left">Vehicle #</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="p-2 border h-12"></td>
                            <td class="p-2 border"></td>
                            <td class="p-2 border"></td>
                        </tr>
                    </tbody>
                </table>

                <table class="w-1/2 md:w-2/5 border border-gray-300 text-sm">
                    <thead class="bg-gray-100 text-left">
                        <tr>
                            <th class="p-2 border w-64 text-left">Prepared By</th>
                            <th class="p-2 border w-40 text-left">Checked By</th>
                            <th class="p-2 border w-40 text-left">Dispatched By</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="p-2 border h-12"></td>
                            <td class="p-2 border"></td>
                            <td class="p-2 border"></td>
                        </tr>
                    </tbody>
                </table>

                <div class="mt-4 flex items-end justify-between">
                    <div>
                        <p class="text-sm">Received in good condition</p>
                        <div class="mt-8 w-64 border-t border-gray-500"></div>
                        <p class="text-xs mt-1">Signature</p>
                    </div>
                    <div class="text-right">
                        <p class="text-sm">Print name:</p>
                        <div class="mt-8 w-48 border-t border-gray-500"></div>
                    </div>
                </div>
            </div>

            <!-- ===== Footer ===== -->
            <div class="mt-6 text-[10px] text-gray-600 flex items-center justify-between">
                <p>Terms and Conditions of Sale on Last Page</p>
                <p>All Unit Prices Subject to the Applicable VAT Rate</p>
            </div>
            <div class="mt-1 text-[10px] text-gray-600 flex items-center justify-between">
                <p>https://www.gumtt.com</p>
            </div>

            <!-- Notes -->
            <div
                v-if="bill.notes"
                class="mt-6"
            >
                <h3 class="font-medium text-sm text-gray-700 mb-1">Notes</h3>
                <p class="p-2 bg-gray-50 rounded text-sm text-gray-600">
                    {{ bill.notes }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
    import print from 'vue-print-nb';
    import QrcodeVue from 'qrcode.vue';
    import common from '../common';
    import ReplaceIcon from '../Components/Icons/ReplaceIcon.vue';
    import PrintIcon from '../Components/Icons/PrintIcon.vue';

    export default {
        name: 'InvoiceComponent',

        directives: {
            print,
        },

        components: {
            QrcodeVue,
            ReplaceIcon,
            PrintIcon,
        },

        props: {
            bill: {
                type: Object,
                required: true,
            },
            invoiceBaseUrl: {
                type: String,
                default: `${process.env.MIX_SENTRY_DSN_PUBLIC}/invoice/`, // adjust per your app
            },
        },

        data() {
            return {
                common,
                printObj: {
                    id: '#print-invoice',
                    popTitle: 'Gumtt-Invoice',
                    extraHead: `
                    <link href="https://unpkg.com/tailwindcss@^2/dist/tailwind.min.css" rel="stylesheet">
                    <style>
                        @media print {
                        body {
                            zoom: 85%;
                        }
                        }
                    </style>
                    `,
                },
            };
        },

        computed: {
            invoiceUrl() {
                return `${this.invoiceBaseUrl}${this.bill.id}`;
            },
        },

        methods: {
            num(v) {
                const n = parseFloat(v || 0);
                return isNaN(n) ? '0.00' : n.toFixed(2);
            },
            reload() {
                window.location.reload();
            },
        },
    };
</script>
